<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <div class="h-panel">
        <b-button v-b-modal.help-modal>Help</b-button>

        <b-button
          v-if="mayStartSeason"
          :to="{name: 'new_season'}"
          variant="primary"
        >
          New season
        </b-button>
      </div>

      <h2>Seasons</h2>

      <p v-if="message !== null">
        {{message}}
      </p>

      <table v-if="isSeasonsVisible" class="table table-bordered text-nowrap">
        <thead class="thead-dark">
          <tr>
            <th scope="col" rowspan="2">Name</th>
            <th scope="col" rowspan="2">Type</th>
            <th scope="col" rowspan="2">Audience</th>
            <th scope="col" rowspan="2">Status</th>
            <th scope="col" colspan="2" class="text-center">Start</th>
            <th scope="col" rowspan="2">Remaining time</th>
            <th scope="col" colspan="2" class="text-center">Finish</th>
            <th scope="col" rowspan="2" v-if="mayManageSeasons">Actions</th>
          </tr>
          <tr>
            <th scope="col">planned</th>
            <th scope="col">real</th>
            <th scope="col">planned</th>
            <th scope="col">real</th>
          </tr>
        </thead>
        <tr
          v-for="season in seasons"
          :key="season.id"
        >
          <td>
            <season-link :seasonId="season.id" />
          </td>
          <td class="season-type-name">
            {{getSeasonTypeName(season.proto_id)}}
          </td>
          <td
            :class="getSeasonForQAClass(season.for_qa)"
          >
            <span v-b-popover.hover.top="getPlayerGroupDescription(season.for_qa)">
              {{season.for_qa | getPlayerGroupName}}
            </span>
          </td>
          <td
            :class="getSeasonStatusClass(season.status)"
          >
            {{season.status | getSeasonStatusName}}
          </td>
          <td>
            {{season.stime | getSeasonRealStartTimeStr}}
          </td>
          <td>
            {{season.srtime | getMomentStr}}
          </td>
          <td>
            {{getRemainingTimeStr(season)}}
          </td>
          <td>
            {{season.ftime | getMomentStr}}
          </td>
          <td>
            {{season.frtime | getSeasonRealFinishTimeStr}}
          </td>
          <td v-if="mayManageSeasons" class="action-box">
            <b-button
              v-if="mustShowStartForAllButton(season)"
              @click="runSeasonForAllWithConfirmation(season)"
              variant="success"
              size="sm"
            >
              Start for all
            </b-button>
            <b-button
              v-if="mustShowFinishButton(season)"
              @click="finalizeSeasonWithConfirmation(season)"
              variant="warning"
              size="sm"
            >
              Finish
            </b-button>
            <b-button
              v-if="mayDeleteSeason"
              @click="deleteSeasonWithConfirmation(season)"
              variant="danger"
              size="sm"
            >
              Delete
            </b-button>
          </td>
        </tr>
      </table>

      <season-table
        v-if="isSeasonsVisible"
        :seasonTypeNameMap="seasonTypeNameMap"
        :seasonContainers="seasonContainers"
        @total-selection-requested="selectAll"
        @total-deselection-requested="deselectAll"
      />

      <loading-indicator
        v-if="isLoading"
      />

    </div>

    <b-modal id="help-modal" title="Help" ok-only ok-title="Close">
      <div class="help">
        <p>
          All seasons are shown on this page.
        </p>
      </div>
    </b-modal>

  </div>
</template>

<script>
import auth from '../auth.js';
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { getPlayerGroupName                     } from '../model/event.js';
import { getPlayerGroupDescription              } from '../model/event.js';

import { SEASON_STATUS_FINISHED                 } from '../model/season.js';
import { getSeasonStatusName                    } from '../model/season.js';
import { getSeasonStatusClass                   } from '../model/season.js';
import { getSeasonForQAClass                    } from '../model/season.js';
import { getSeasonRealStartTimeStr              } from '../model/season.js';
import { getSeasonRealFinishTimeStr             } from '../model/season.js';
import { getSeasonRemainingTimeStr              } from '../model/season.js';
import { isSeasonActive                         } from '../model/season.js';
import { isSeasonForQA                          } from '../model/season.js';
import { convertSeasonTypeArrayToTypeNameMap    } from '../model/season.js';
import { getSeasonTypeNameFromMap               } from '../model/season.js';

import { mayManageSeasons as doMayManageSeasons } from '../model/season.js';
import { mayStartSeason as doMayStartSeason     } from '../model/season.js';
import { mayFinalizeSeason                      } from '../model/season.js';
import { mayDeleteSeason as doMayDeleteSeason   } from '../model/season.js';

import { confirmAction } from '../service/confirm.js';

import { arrayDeleteFirstItemByValue } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import SeasonTable from './SeasonTable.vue';
import SeasonLink from './SeasonLink.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'Seasons',

  components: {
    MyHeader,
    LoadingIndicator,
    SeasonTable,
    SeasonLink,
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,
      seasonTypeNameMap: null,
      seasons: null,
      seasonContainers: null,

      timerId: null,
      nowSec: Date.now() / 1000,
    };
  },

  computed: {  
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const containers = this.seasonContainers;
      if(containers === null) {
        return null;
      }

      if(containers.length === 0) {
        return 'No seasons.';
      }

      return null;
    },

    /** @returns {boolean} */
    isSeasonsVisible() {
      const containers = this.seasonContainers;
      if(containers === null) {
        return null;
      }

      return containers.length > 0;
    },

    /** @returns {boolean} */
    mayManageSeasons() {
      return doMayManageSeasons(this.actor.role);
    },

    /** @returns {boolean} */
    mayStartSeason() {
      return doMayStartSeason(this.actor.role);
    },

    /** @returns {boolean} */
    mayDeleteSeason() {
      return doMayDeleteSeason(this.actor.role);
    },
  },

  created() {
    this.timerId = setInterval(this.updateTime, 1000);

    this.loadSeasons();
  },

  beforeDestroy() {
    if(this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  },

  methods: {
    updateTime() {
      this.nowSec = Date.now() / 1000;
    },

    loadSeasons() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/seasons';
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.seasonTypeNameMap = convertSeasonTypeArrayToTypeNameMap(data.seasonTypes);

        const seasons = data.seasons;
        seasons.sort((a, b) => b.stime - a.stime);

        this.seasons = seasons;
        this.seasonContainers = convertSeasonsToSeasonContainers(seasons);

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e.response.data, {sender: this});
      });
    },

    getSeasonTypeName(seasonProtoId) {
      return getSeasonTypeNameFromMap(this.seasonTypeNameMap, seasonProtoId);
    },

    getSeasonStatusClass,
    getSeasonForQAClass,

    getPlayerGroupDescription,

    getRemainingTimeStr(season) {
      return getSeasonRemainingTimeStr(season, this.nowSec);
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectAll() {
      for(const container of this.seasonContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectAll() {
      for(const container of this.seasonContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------

    mustShowStartForAllButton(season) {
      if(!doMayManageSeasons(this.actor.role)) {
        return false;
      }
      return isSeasonForQA(season.for_qa);
    },

    runSeasonForAllWithConfirmation(season) {
      this.confirmRunningSeasonForAll(() => {
        this.runForAll(season);
      });
    },

    confirmRunningSeasonForAll(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to run for all this season {env}?',
        'Run',
        onConfirmed,
      );
    },

    runForAll(season) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/season/${season.id}/run_for_all`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        season.for_qa = 0;
        notifier.showSuccess('Season runned for all.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    mustShowFinishButton(season) {
      if(!mayFinalizeSeason(this.actor.role)) {
        return false;
      }
      return isSeasonActive(season.status);
    },

    finalizeSeasonWithConfirmation(season) {
      this.confirmSeasonFinalization(() => {
        this.finalizeSeason(season);
      });
    },

    // function onConfirmed()
    confirmSeasonFinalization(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to finish this season {env}?',
        'Finish',
        onConfirmed,
      );
    },

    finalizeSeason(season) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/season/${season.id}/finalize`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        season.frtime = data.frtime;
        season.status = SEASON_STATUS_FINISHED;
        notifier.showSuccess('Season finished.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    deleteSeasonWithConfirmation(season) {
      notifier.deleteSenderNotifications(this);

      this.confirmSeasonDeletion(() => {
        this.deleteSeason(season);
      });
    },

    // function onConfirmed()
    confirmSeasonDeletion(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to delete this season {env}?',
        'Delete',
        onConfirmed,
      );
    },

    deleteSeason(season) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/season/${season.id}/del`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        arrayDeleteFirstItemByValue(this.seasons, season);
        notifier.showSuccess('Season deleted.');
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },

  filters: {
    getPlayerGroupName,
    getSeasonStatusName,
    getSeasonRealStartTimeStr,
    getSeasonRealFinishTimeStr,
  },
};

function convertSeasonsToSeasonContainers(seasons) {
  const containers = [];

  for(const season of seasons) {
    const container = {
      season: season,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}
</script>

<style scoped>
.season-type-name {
  font-weight: bold;
}

.action-box {
  padding: 7px;
  vertical-align: middle;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
